import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { theme } from '../constants/theme';
import useAILinkCoder from '../hooks/useAILinkCoder';

const StyledButton = styled(Button)(() => ({
  backgroundColor: theme.colors.primary,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 24px',
  '&:hover': {
    backgroundColor: '#c73e24',
  },
}));

const AILinkCoderDialog = ({ isOpen, onClose, onApply, fieldConfig, language = 'nl' }) => {
  const { generateEntries } = useAILinkCoder();
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedEntries, setGeneratedEntries] = useState(null);
  const [errors, setErrors] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);

  // Text translations
  const text = {
    nl: {
      title: 'AI Campagne Generator',
      instructionsLabel: 'Instructies voor AI',
      instructionsPlaceholder: 'Beschrijf welke campagne entries je wilt genereren. Bijvoorbeeld: "Maak 5 Facebook campagnes voor zomer promotie" of "Genereer 3 LinkedIn B2B leads campagnes"',
      instructionsHelper: 'Geef duidelijke instructies over hoeveel entries je wilt en voor welk doel',
      generate: 'Genereer Entries',
      generating: 'AI genereert campagne entries...',
      preview: 'Preview van gegenereerde entries',
      validationErrors: 'Validatie fouten',
      generatedSuccess: 'Entries succesvol gegenereerd!',
      addEntries: 'Voeg Entries Toe',
      cancel: 'Annuleren',
      close: 'Sluiten',
      tryAgain: 'Opnieuw proberen',
      noFieldsEnabled: 'Geen velden ingeschakeld. Schakel eerst velden in bij Opties.',
      entriesPreview: 'Gegenereerde entries',
      entriesCount: 'entries'
    },
    en: {
      title: 'AI Campaign Generator',
      instructionsLabel: 'Instructions for AI',
      instructionsPlaceholder: 'Describe which campaign entries you want to generate. For example: "Create 5 Facebook campaigns for summer promotion" or "Generate 3 LinkedIn B2B lead campaigns"',
      instructionsHelper: 'Provide clear instructions about how many entries you want and for what purpose',
      generate: 'Generate Entries',
      generating: 'AI is generating campaign entries...',
      preview: 'Preview of generated entries',
      validationErrors: 'Validation errors',
      generatedSuccess: 'Entries successfully generated!',
      addEntries: 'Add Entries',
      cancel: 'Cancel',
      close: 'Close',
      tryAgain: 'Try again',
      noFieldsEnabled: 'No fields enabled. Please enable fields in Options first.',
      entriesPreview: 'Generated entries',
      entriesCount: 'entries'
    }
  };

  const t = text[language] || text.nl;

  const handleGenerate = async () => {
    if (!instructions.trim()) {
      return;
    }

    setLoading(true);
    setErrors([]);
    setGeneratedEntries(null);

    const result = await generateEntries(instructions, fieldConfig);

    setLoading(false);

    if (result.success) {
      setGeneratedEntries(result.entries);
      setPreviewMode(true);
      setErrors([]);
    } else {
      setErrors(result.errors);
      setGeneratedEntries(result.entries);
      setPreviewMode(true);
    }
  };

  const handleApply = () => {
    if (generatedEntries && errors.length === 0) {
      onApply(generatedEntries);
      handleClose();
    }
  };

  const handleClose = () => {
    setInstructions('');
    setGeneratedEntries(null);
    setErrors([]);
    setPreviewMode(false);
    setLoading(false);
    onClose();
  };

  const handleTryAgain = () => {
    setPreviewMode(false);
    setGeneratedEntries(null);
    setErrors([]);
  };

  // Convert HTML clipboard content to plain text while keeping link URL's
  const htmlToTextWithLinks = (html) => {
    if (!html) return '';

    const container = document.createElement('div');
    container.innerHTML = html;

    const parts = [];

    const getImageDimensions = (el) => {
      if (!el) return null;

      const parseSize = (value) => {
        if (!value) return null;
        const match = String(value).match(/(\d+)\s*px?$/i);
        return match ? parseInt(match[1], 10) : null;
      };

      let width = parseSize(el.getAttribute('width'));
      let height = parseSize(el.getAttribute('height'));

      const styleAttr = el.getAttribute('style') || '';
      if (!width) {
        const mw = styleAttr.match(/width:\s*(\d+)\s*px/i);
        if (mw) width = parseInt(mw[1], 10);
      }
      if (!height) {
        const mh = styleAttr.match(/height:\s*(\d+)\s*px/i);
        if (mh) height = parseInt(mh[1], 10);
      }

      // Probeer voor SVG nog de viewBox te gebruiken als width/height ontbreken
      if ((!width || !height) && el.tagName === 'SVG') {
        const viewBox = el.getAttribute('viewBox');
        if (viewBox) {
          const parts = viewBox.split(/\s+/);
          if (parts.length === 4) {
            const vbWidth = parseInt(parts[2], 10);
            const vbHeight = parseInt(parts[3], 10);
            if (!isNaN(vbWidth) && !isNaN(vbHeight)) {
              width = width || vbWidth;
              height = height || vbHeight;
            }
          }
        }
      }

      if (width && height) {
        return { width, height };
      }
      return null;
    };

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parts.push(node.nodeValue);
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const tag = node.tagName;

      if (tag === 'BR') {
        parts.push('\n');
        return;
      }

      if (tag === 'P' || tag === 'DIV' || tag === 'LI') {
        // Begin block
        if (parts.length && !parts[parts.length - 1].endsWith('\n')) {
          parts.push('\n');
        }
        node.childNodes.forEach(walk);
        // End block
        if (!parts[parts.length - 1].endsWith('\n')) {
          parts.push('\n');
        }
        return;
      }

      if (tag === 'A') {
        const text = (node.textContent || '').trim();
        const href = node.getAttribute('href') || '';
        if (!href) {
          parts.push(text);
          return;
        }

        const mediaEl = node.querySelector && node.querySelector('img, svg');
        const isImageLink = !!mediaEl;
        const dims = getImageDimensions(mediaEl);

        const linkText = text || href;
        let meta = `linktekst: ${linkText}; url: ${href}; type: ${isImageLink ? 'afbeelding' : 'tekstlink'}`;
        if (isImageLink && dims) {
          meta += `; afbeeldingsgrootte: ${dims.width} x ${dims.height} px`;
        }

        parts.push(`${linkText} (${meta})`);
        return;
      }

      // Default: recurse into children
      node.childNodes.forEach(walk);
    };

    container.childNodes.forEach(walk);

    let result = parts.join('');
    // Collapse meer dan 2 lege regels
    result = result.replace(/\n{3,}/g, '\n\n');
    return result.trimEnd();
  };

  const handlePasteInstructions = (event) => {
    if (!event.clipboardData) {
      return;
    }

    const html = event.clipboardData.getData('text/html');
    if (!html) {
      // Laat de browser standaard gedrag doen (plain text)
      return;
    }

    event.preventDefault();

    const pastedText = htmlToTextWithLinks(html);

    const target = event.target;
    const selectionStart = target.selectionStart ?? instructions.length;
    const selectionEnd = target.selectionEnd ?? instructions.length;

    const before = instructions.slice(0, selectionStart);
    const after = instructions.slice(selectionEnd);

    setInstructions(before + pastedText + after);
  };

  // Get visible columns from field config
  const getVisibleColumns = () => {
    if (!fieldConfig) return [];
    
    return Object.entries(fieldConfig.fields)
      .filter(([_, config]) => config.enabled)
      .sort((a, b) => a[1].gridOrder - b[1].gridOrder)
      .map(([fieldName, config]) => ({
        name: fieldName,
        displayName: config.displayName
      }));
  };

  // Check if any fields are enabled
  const hasEnabledFields = fieldConfig && Object.values(fieldConfig.fields).some(f => f.enabled);

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${theme.borderRadius.lg}px`,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        pb: 2
      }}>
        {t.title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: theme.colors.textSecondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ paddingTop: '24px !important', minHeight: 300 }}>
        {!hasEnabledFields ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t.noFieldsEnabled}
          </Alert>
        ) : !previewMode ? (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              label={t.instructionsLabel}
              placeholder={t.instructionsPlaceholder}
              helperText={t.instructionsHelper}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onPaste={handlePasteInstructions}
              disabled={loading}
              autoFocus
            />

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, justifyContent: 'center' }}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">{t.generating}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            {errors.length > 0 ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t.validationErrors}:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t.generatedSuccess}
              </Alert>
            )}

            {generatedEntries && (
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  {t.entriesPreview}: {generatedEntries.length} {t.entriesCount}
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 2 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {getVisibleColumns().map((col) => (
                          <TableCell key={col.name} sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                            {col.displayName}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {generatedEntries.map((entry, rowIndex) => (
                        <TableRow key={rowIndex} hover>
                          {getVisibleColumns().map((col) => (
                            <TableCell key={col.name}>
                              {entry[col.name] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 3, borderTop: `1px solid ${theme.colors.border}` }}>
        {!previewMode ? (
          <>
            <Button onClick={handleClose} sx={{ color: theme.colors.textSecondary }}>
              {t.cancel}
            </Button>
            <StyledButton 
              onClick={handleGenerate}
              disabled={!instructions.trim() || loading || !hasEnabledFields}
            >
              {t.generate}
            </StyledButton>
          </>
        ) : (
          <>
            <Button onClick={handleTryAgain} sx={{ color: theme.colors.textSecondary }}>
              {t.tryAgain}
            </Button>
            {errors.length === 0 && (
              <StyledButton onClick={handleApply}>
                {t.addEntries}
              </StyledButton>
            )}
            {errors.length > 0 && (
              <Button onClick={handleClose}>
                {t.close}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AILinkCoderDialog;
